"use client";

import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { enUS, es, fr } from "date-fns/locale";
import {
  Building2,
  CalendarClock,
  Clock,
  History,
  Loader2,
  Pencil,
  Play,
  Radio,
  Table2,
  Trash2,
  User,
} from "lucide-react";

import { Badge } from "@stockline/ui/components/badge";
import { Button } from "@stockline/ui/components/button";
import { Switch } from "@stockline/ui/components/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@stockline/ui/components/alert-dialog";

import { describeDefinition } from "./summary";
import type { TriggerType, TriggerWithWorkflow } from "./types";

const DATE_LOCALES = { fr, es, en: enUS } as const;

export function dateLocaleFor(language: string) {
  const lang = language.split("-")[0] as keyof typeof DATE_LOCALES;
  return DATE_LOCALES[lang] ?? enUS;
}

const TYPE_META: Record<
  TriggerType,
  { icon: typeof Radio; className: string }
> = {
  event: {
    icon: Radio,
    className:
      "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-transparent",
  },
  schedule: {
    icon: CalendarClock,
    className:
      "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300 border-transparent",
  },
  poll: {
    icon: Table2,
    className:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border-transparent",
  },
};

interface TriggerCardProps {
  trigger: TriggerWithWorkflow;
  /** Saved-query title for poll summaries (resolved by the panel). */
  queryTitle?: string;
  /** Whether the viewer can edit/toggle/delete this trigger. */
  canManage: boolean;
  /** Whether the viewer can manually fire it (manager/admin only). */
  canRun: boolean;
  togglePending: boolean;
  runPending: boolean;
  onToggleActive: (isActive: boolean) => void;
  onRunNow: () => void;
  onEdit: () => void;
  onShowHistory: () => void;
  onDelete: () => void;
}

export function TriggerCard({
  trigger,
  queryTitle,
  canManage,
  canRun,
  togglePending,
  runPending,
  onToggleActive,
  onRunNow,
  onEdit,
  onShowHistory,
  onDelete,
}: TriggerCardProps) {
  const { t, i18n } = useTranslation();
  const meta = TYPE_META[trigger.triggerType];
  const TypeIcon = meta.icon;

  const summary = describeDefinition(trigger, {
    locale: i18n.language,
    queryTitle,
  });

  const nextFire = trigger.nextFireAt ? new Date(trigger.nextFireAt) : null;
  // A fire advances `nextFireAt` server-side; until the SSE-driven refresh
  // lands, a just-passed time would otherwise render as "next run … ago".
  const nextFireIsDue = nextFire != null && nextFire.getTime() <= Date.now();

  return (
    <div
      className="flex items-start justify-between gap-4 rounded-lg border bg-card p-4"
      data-testid={`trigger-card-${trigger.id}`}
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`gap-1 ${meta.className}`}>
            <TypeIcon className="h-3 w-3" />
            {t(`ai.triggersPanel.types.${trigger.triggerType}`)}
          </Badge>
          <span className="truncate font-medium">
            {trigger.workflowName ?? t("ai.triggersPanel.unknownWorkflow")}
          </span>
          <Badge variant="outline" className="gap-1 font-normal">
            {trigger.scope === "company" ? (
              <Building2 className="h-3 w-3" />
            ) : (
              <User className="h-3 w-3" />
            )}
            {trigger.scope === "company"
              ? t("ai.triggersPanel.scopeCompany")
              : t("ai.triggersPanel.scopePersonal")}
          </Badge>
        </div>

        <p className="text-sm text-muted-foreground">
          {t(`ai.triggersPanel.summary.${summary.key}`, summary.params)}
        </p>

        {trigger.conditionDsl && (
          <code className="inline-block max-w-full truncate rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
            {trigger.conditionDsl}
          </code>
        )}

        {nextFire && trigger.isActive && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {nextFireIsDue
              ? t("ai.triggersPanel.nextFireDue")
              : t("ai.triggersPanel.nextFire", {
                  relative: formatDistanceToNow(nextFire, {
                    addSuffix: true,
                    locale: dateLocaleFor(i18n.language),
                  }),
                  absolute: nextFire.toLocaleString(i18n.language, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }),
                })}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Switch
          checked={trigger.isActive}
          onCheckedChange={onToggleActive}
          disabled={!canManage || togglePending}
          aria-label={t("ai.triggersPanel.active")}
          data-testid="trigger-active-switch"
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onShowHistory}
          aria-label={t("ai.triggersPanel.history.open")}
          title={t("ai.triggersPanel.history.open")}
        >
          <History className="h-4 w-4" />
        </Button>
        {canRun && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onRunNow}
            disabled={runPending}
            aria-label={t("ai.triggersPanel.runNow")}
            title={t("ai.triggersPanel.runNow")}
            data-testid="trigger-run-now"
          >
            {runPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
        )}
        {canManage && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onEdit}
              aria-label={t("ai.triggersPanel.edit")}
              title={t("ai.triggersPanel.edit")}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  aria-label={t("ai.triggersPanel.delete")}
                  title={t("ai.triggersPanel.delete")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("ai.triggersPanel.deleteConfirmTitle")}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("ai.triggersPanel.deleteConfirmDescription")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    {t("ai.triggersPanel.editor.cancel")}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t("ai.triggersPanel.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
}
